import type { ApolloError } from '@apollo/client';
import {
  type GetSiteLayoutConnectionsQuery,
  type LayoutConnectionFieldsFragment,
  ZonesMode,
} from 'apollo/generated/client-operations';
import classNames from 'classnames';
import React from 'react';

/** What one read of the effective layout says about a zone's or entry side's Avigilon connection. */
export type ConnectionRead =
  | { state: 'loading' }
  /** `message` is the field's own error; absent when the read as a whole cannot be trusted. */
  | { state: 'failed'; message?: string }
  | { state: 'loaded'; connection: LayoutConnectionFieldsFragment | null };

export type ConnectionReads = {
  /**
   * Set when the read is of a revision other than the designer's effective
   * one (null: of no published layout): the layout and the read are both to
   * be read again.
   */
  stale: { revisionId: string | null } | null;
  of: (id: string) => ConnectionRead;
};

const failedRead: ConnectionRead = { state: 'failed' };
const loadingRead: ConnectionRead = { state: 'loading' };

/**
 * The connection read for each zone and entry side of the site's effective
 * layout, by id. Only a read of this site's effective revision is used; until
 * one arrives every id is loading. An error on one zone's or side's
 * connection (a zone with more than one Avigilon zone connection) fails that
 * id alone. Any other error (a network failure, or an error at a path that is
 * not one id's connection) fails every id, even over an earlier result. An id
 * the read does not name is failed too: none of these is "not created".
 */
export const connectionReadsOf = (
  siteId: string,
  effectiveRevisionId: string | null,
  data: GetSiteLayoutConnectionsQuery | undefined,
  error: ApolloError | undefined,
): ConnectionReads => {
  const site =
    data?.getJobsite?.jobsiteId === siteId ? data.getJobsite : undefined;
  const layout = site?.siteLayout ?? null;

  // Paths: getJobsite.siteLayout.zones.<i>.connection and
  // getJobsite.siteLayout.entries.<i>.sideA|sideB.connection.
  const failures = new Map<string, string>();
  let unattributed = !!error && !error.graphQLErrors.length;
  error?.graphQLErrors.forEach(({ path, message }) => {
    const [, , list, index, field, sideField] = path ?? [];
    let id: string | undefined;
    if (layout && typeof index === 'number') {
      if (list === 'zones' && field === 'connection')
        id = layout.zones[index]?.id;
      else if (
        list === 'entries' &&
        (field === 'sideA' || field === 'sideB') &&
        sideField === 'connection'
      )
        id = layout.entries[index]?.[field].id;
    }
    if (id) failures.set(id, message);
    else unattributed = true;
  });

  if (unattributed) return { stale: null, of: () => failedRead };
  if (!site) return { stale: null, of: () => loadingRead };
  const revisionId = layout?.revisionId ?? null;
  if (revisionId !== effectiveRevisionId)
    return { stale: { revisionId }, of: () => loadingRead };

  const connections = new Map<string, LayoutConnectionFieldsFragment | null>();
  layout?.zones.forEach((zone) =>
    connections.set(zone.id, zone.connection ?? null),
  );
  layout?.entries.forEach((entry) =>
    [entry.sideA, entry.sideB].forEach((side) =>
      connections.set(side.id, side.connection ?? null),
    ),
  );
  return {
    stale: null,
    of: (id: string): ConnectionRead => {
      const failure = failures.get(id);
      if (failure !== undefined) return { state: 'failed', message: failure };
      const connection = connections.get(id);
      return connection === undefined
        ? failedRead
        : { state: 'loaded', connection };
    },
  };
};

export type AvigilonStatusLine = {
  label?: string;
  text: string;
  failed?: boolean;
};

const LOADING_TEXT = 'Loading the Avigilon status…';
const READ_FAILED_TEXT = 'Could not load the Avigilon status.';
const NOT_CREATED_TEXT = 'Not yet created in Avigilon.';
const NO_ENTRY_NEEDED_TEXT =
  'No reader admits into this side, so it needs no Avigilon entry.';

const connectionLine = (
  read: ConnectionRead,
  resourceName: string,
  notConnectedText: string,
): AvigilonStatusLine => {
  if (read.state === 'loading') return { text: LOADING_TEXT };
  if (read.state === 'failed')
    return {
      text: read.message
        ? `Could not read the ${resourceName}. ${read.message}`
        : READ_FAILED_TEXT,
      failed: true,
    };
  if (!read.connection) return { text: notConnectedText };
  const { name, id } = read.connection.externalEntity;
  return { text: `${name} · ID ${id}` };
};

export const avigilonZoneLine = (read: ConnectionRead): AvigilonStatusLine =>
  connectionLine(read, 'Avigilon zone', NOT_CREATED_TEXT);

/**
 * Delivery creates an Avigilon entry only for a side a reader admits into: a
 * side none does (an entrance-only entry's way out) needs none.
 */
export const avigilonEntrySideLine = (
  read: ConnectionRead,
  hasReader: boolean,
): AvigilonStatusLine =>
  connectionLine(
    read,
    'Avigilon entry',
    hasReader ? NOT_CREATED_TEXT : NO_ENTRY_NEEDED_TEXT,
  );

/** The site's zones mode as read, or that the read is loading or failed. */
export type ZonesModeRead = ZonesMode | 'loading' | 'failed';

/**
 * A zone or entry side new in the draft has nothing in Avigilon. Delivery
 * creates it after the draft is published, and writes nothing while the
 * site's zones are off. While the mode is unknown, neither is promised.
 */
const draftLine = (zonesMode: ZonesModeRead): AvigilonStatusLine => {
  if (zonesMode === 'loading') return { text: LOADING_TEXT };
  if (zonesMode === 'failed') return { text: READ_FAILED_TEXT, failed: true };
  return {
    text:
      zonesMode === ZonesMode.Off
        ? 'Created in Avigilon only once it is published and zones are enabled for this site.'
        : 'Created in Avigilon after the next publish while zones are on.',
  };
};

export const draftZoneLine = (zonesMode: ZonesModeRead): AvigilonStatusLine =>
  draftLine(zonesMode);

export const draftEntrySideLine = (
  zonesMode: ZonesModeRead,
  hasReader: boolean,
): AvigilonStatusLine =>
  hasReader ? draftLine(zonesMode) : { text: NO_ENTRY_NEEDED_TEXT };

export type AvigilonStatusProps = {
  title: string;
  /** Says which revision the lines describe, where that is not the one shown. */
  note?: string;
  lines: AvigilonStatusLine[];
};

const classes = {
  section: 'odin-flex odin-flex-col odin-gap-2',
  sectionTitle:
    'odin-text-xs odin-font-semibold odin-uppercase odin-tracking-wide odin-text-gray-500',
  note: 'odin-text-xs odin-text-gray-500',
  line: (failed?: boolean): string =>
    classNames(
      'odin-text-sm',
      failed ? 'odin-text-red-600' : 'odin-text-gray-700',
    ),
  label: 'odin-font-medium odin-text-gray-900',
};

/** The Avigilon zone or entries a selected zone or entry has, as Odin knows them. */
export function AvigilonStatus({
  title,
  note,
  lines,
}: AvigilonStatusProps): React.ReactElement {
  return (
    <section className={classes.section} aria-label={title}>
      <h3 className={classes.sectionTitle}>{title}</h3>
      {note && <p className={classes.note}>{note}</p>}
      {lines.map((line) => (
        <p key={line.label ?? line.text} className={classes.line(line.failed)}>
          {line.label && <span className={classes.label}>{line.label}: </span>}
          {line.text}
        </p>
      ))}
    </section>
  );
}
